import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from 'react'
import { supabase } from '../lib/supabase'
import { queryClient } from '../lib/queryClient'
import type { User, Session } from '@supabase/supabase-js'

// Discord configuration. The server, officer and apothecary ids now live in
// the auth-sync edge function, which is where roles are decided - the browser
// only needs to recognise a stored membership.
const MEMBER_ROLE_ID = '1196897301461995580'

export interface UserAccount {
  id: string
  discord_user_id: string | null
  discord_username: string | null
  discord_avatar: string | null
  discord_roles: string[]
  is_officer: boolean
  is_apothecary: boolean
  bio: string | null
  location_lat: number | null
  location_lng: number | null
  location_name: string | null
  timezone: string | null
  profile_layout: string[] | null
  created_at: string
  updated_at: string
}

/**
 * Someone who confirmed their Discord account to send an application, not a
 * member. See startApplicantSignIn.
 */
export interface ApplicantIdentity {
  discordUsername: string | null
  avatarUrl: string | null
}

interface AuthContextType {
  user: User | null
  /** Set only for the few seconds an applicant is signed in to submit */
  applicant: ApplicantIdentity | null
  session: Session | null
  userAccount: UserAccount | null
  isOfficer: boolean
  isApothecary: boolean
  loading: boolean
  authError: string | null
  signInWithDiscord: () => Promise<void>
  startApplicantSignIn: () => Promise<void>
  endApplicantSession: () => Promise<void>
  signOut: () => Promise<void>
  updateBio: (bio: string) => Promise<void>
  updateLocation: (lat: number, lng: number, locationName: string, timezone: string) => Promise<void>
  removeLocation: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

/**
 * Ask the server to re-read this user's Discord roles and write them.
 *
 * The browser used to do this itself, which meant it also wrote is_officer -
 * and anything the browser can write, a member can write from the console.
 * Migration 110 locks those columns to the service role, so auth-sync is the
 * only thing that can set them now.
 *
 * Returns the account plus what the server managed to confirm:
 *   member === false  the user is definitely not in the guild (row removed)
 *   account present   use it
 *   null              the function could not be reached; trust what we have
 */
// A Discord token can linger in the stored session after sign-in, so the
// sync is throttled per user rather than run on every page load. A failed
// sync still counts: if Discord is rate-limiting us, retrying immediately on
// the next page load is the wrong move.
const SYNC_INTERVAL_MS = 15 * 60 * 1000

function syncKey(userId: string) {
  return `raven:lastAuthSync:${userId}`
}

function shouldSyncAccount(userId: string): boolean {
  try {
    const last = Number(window.localStorage.getItem(syncKey(userId)) || 0)
    return Date.now() - last > SYNC_INTERVAL_MS
  } catch {
    // Private windows and blocked storage: sync rather than skip
    return true
  }
}

function markAccountSynced(userId: string) {
  try {
    window.localStorage.setItem(syncKey(userId), String(Date.now()))
  } catch {
    // Nothing to do - the throttle is an optimisation, not a correctness need
  }
}

async function syncAccount(providerToken: string): Promise<{
  account: UserAccount | null
  member?: boolean
  verified?: boolean
} | null> {
  try {
    const { data, error } = await supabase.functions.invoke('auth-sync', {
      body: { provider_token: providerToken },
    })
    if (error) {
      console.error('[Auth] auth-sync failed:', error.message)
      return null
    }
    return data
  } catch (err) {
    console.error('[Auth] auth-sync unreachable:', err)
    return null
  }
}

/**
 * Applicants sign in with Discord only to prove who they are, then get
 * signed straight out. The flag survives the round trip to Discord (same
 * tab), and while it is set a session is an applicant, never a member: it
 * does not get a user, an account or any member page.
 *
 * That matters because RLS treats every signed-in user as a member - an
 * applicant must not sit on a live session while browsing the site.
 */
const APPLYING_KEY = 'raven:applying'

function isApplying(): boolean {
  try {
    return window.sessionStorage.getItem(APPLYING_KEY) === '1'
  } catch {
    return false
  }
}

function setApplying(on: boolean) {
  try {
    if (on) window.sessionStorage.setItem(APPLYING_KEY, '1')
    else window.sessionStorage.removeItem(APPLYING_KEY)
  } catch {
    // Without storage the applicant comes back as a would-be member, is
    // refused as a non-member and signed out - safe, just unhelpful
  }
}

function applicantFromSession(session: Session): ApplicantIdentity {
  const meta = session.user.user_metadata ?? {}
  return {
    discordUsername: meta.full_name ?? meta.name ?? null,
    avatarUrl: meta.avatar_url ?? null,
  }
}

/**
 * Check if role IDs include the Member role
 */
function checkIsMember(roleIds: string[]): boolean {
  return roleIds.includes(MEMBER_ROLE_ID)
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [applicant, setApplicant] = useState<ApplicantIdentity | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [userAccount, setUserAccount] = useState<UserAccount | null>(null)
  const [loading, setLoading] = useState(true)
  const [authError, setAuthError] = useState<string | null>(null)

  // Refs for debouncing - avoid stale closures in event handlers
  const lastFetchTimeRef = useRef(0)
  const isFetchingRef = useRef(false)
  const currentUserIdRef = useRef<string | null>(null)
  const hasHadInitialSessionRef = useRef(false)
  const userAccountRef = useRef<UserAccount | null>(null) // Track current account to avoid stale closure

  // Fetch or create user account - simplified and bulletproof
  const fetchOrCreateUserAccount = async (authUser: User, authSession: Session | null): Promise<UserAccount | null> => {
    console.log('[Auth] fetchOrCreateUserAccount starting for user:', authUser.id)
    try {
      // Try to fetch existing account with timeout
      console.log('[Auth] Querying user_accounts...')

      const queryPromise = supabase
        .from('user_accounts')
        .select('*')
        .eq('id', authUser.id)
        .single()

      // Add 30 second timeout to handle Supabase cold starts
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('QUERY_TIMEOUT')), 30000)
      })

      const { data: existingAccount, error: fetchError } = await Promise.race([
        queryPromise,
        timeoutPromise
      ])

      console.log('[Auth] Query result:', { found: !!existingAccount, ...(fetchError && { error: fetchError.code }) })

      // Account exists - verify membership
      if (existingAccount) {
        console.log('[Auth] Found existing account')

        // Always check stored roles - block accounts without member role
        const storedRoles = existingAccount.discord_roles || []
        if (storedRoles.length === 0 || !checkIsMember(storedRoles)) {
          console.log('[Auth] Existing account has no valid membership - blocking')
          // Delete the invalid account
          await supabase.from('user_accounts').delete().eq('id', authUser.id)
          throw new Error('NOT_MEMBER')
        }

        // Re-verify whenever we hold a Discord token, throttled per user.
        //
        // This used to be gated on "code=" being in the URL, which never
        // fired: the account fetch is deliberately deferred out of the
        // onAuthStateChange callback (see loadAccount below), and by the time
        // it runs supabase-js has already stripped the OAuth parameters from
        // the address bar. A provider token is only present on a fresh sign-in
        // anyway, so it is the better signal - and the throttle keeps a
        // lingering token from calling Discord on every page load.
        const providerToken = authSession?.provider_token
        if (providerToken && shouldSyncAccount(authUser.id)) {
          console.log('[Auth] Verifying guild membership via auth-sync...')
          markAccountSynced(authUser.id)

          // auth-sync re-reads the roles from Discord and writes them. The
          // browser cannot write is_officer any more (migration 110) - if it
          // could, anyone could grant it to themselves from the console.
          const result = await syncAccount(providerToken)

          // Confirmed not a member: the function has already removed the row
          if (result?.member === false) {
            console.log('[Auth] User is no longer a guild member - blocking')
            throw new Error('NOT_MEMBER')
          }

          // Rate limited or Discord unwell: keep the stored account rather
          // than locking a real member out of their own guild site
          if (result?.account) return result.account
        }

        return existingAccount
      }

      // No account found (PGRST116) - auth-sync creates it, because only the
      // service role may write the Discord identity and rank columns
      if (fetchError?.code === 'PGRST116') {
        const providerToken = authSession?.provider_token

        // Must have a token to verify membership for new users
        if (!providerToken) {
          console.log('[Auth] No provider token for new user - blocking')
          throw new Error('NOT_MEMBER')
        }

        const result = await syncAccount(providerToken)

        // A new account is only created on a confirmed membership. Anything
        // unconfirmed - rate limited, Discord down, function unreachable -
        // blocks rather than guessing.
        if (!result?.account) {
          console.log('[Auth] Could not verify membership for new user - blocking')
          throw new Error('NOT_MEMBER')
        }

        return result.account
      }

      // Some other error
      console.error('Error fetching user account:', fetchError)
      return null
    } catch (err) {
      if (err instanceof Error && err.message === 'NOT_MEMBER') {
        throw err // Re-throw to handle in caller
      }
      if (err instanceof Error && err.message === 'QUERY_TIMEOUT') {
        console.error('[Auth] Query timed out - Supabase may be unreachable')
        return null
      }
      console.error('Error in fetchOrCreateUserAccount:', err)
      return null
    }
  }

  useEffect(() => {
    let mounted = true

    const initAuth = async () => {
      try {
        console.log('[Auth] Getting session...')

        // Get current session
        const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession()

        console.log('[Auth] Session result:', {
          hasSession: !!currentSession,
          hasUser: !!currentSession?.user,
          ...(sessionError && { error: sessionError.message })
        })

        if (!mounted) return

        if (sessionError) {
          console.error('[Auth] Session error:', sessionError)
          await supabase.auth.signOut()
          setLoading(false)
          return
        }

        // No session - show login
        if (!currentSession?.user) {
          console.log('[Auth] No session, showing login')
          setLoading(false)
          return
        }

        // An applicant back from Discord: never a member session
        if (isApplying()) {
          setApplicant(applicantFromSession(currentSession))
          setLoading(false)
          return
        }

        // Have session - just set user/session, let onAuthStateChange handle account fetch
        // This avoids duplicate fetches since INITIAL_SESSION will fire
        console.log('[Auth] Session found, waiting for INITIAL_SESSION event to fetch account')
        setSession(currentSession)
        setUser(currentSession.user)
        // Don't set loading false here - let the event handler do it after fetching account
      } catch (err) {
        // Ignore AbortError - happens during React Strict Mode remounts
        if (err instanceof Error && err.name === 'AbortError') return
        console.error('Auth init error:', err)
        if (mounted) {
          setLoading(false)
        }
      }
    }

    initAuth()

    // Check if this is an OAuth callback (has code in URL)
    const isOAuthCallback = window.location.search.includes('code=')

    // Fetches the user account for a session. Must run OUTSIDE the
    // onAuthStateChange callback: supabase-js holds its auth lock while the
    // callback runs, and any query awaited inside it waits for that same lock.
    // On TOKEN_REFRESHED (hourly, or when a backgrounded tab wakes up) that
    // deadlocked every request on the page until the 30s timeout or an F5.
    const loadAccount = async (newSession: Session) => {
      try {
        const account = await fetchOrCreateUserAccount(newSession.user, newSession)
        console.log('[Auth] Got account:', !!account)
        if (mounted) {
          // Only update userAccount if we got a valid account,
          // or if we don't have one yet. Don't clear existing account on timeout.
          if (account) {
            // First account for this session: anything cached before login
            // (or for another user) was fetched without the right access
            if (!userAccountRef.current) void queryClient.invalidateQueries()
            setUserAccount(account)
            userAccountRef.current = account
            setAuthError(null)
          } else if (!userAccountRef.current) {
            // Only log warning if we don't already have a cached account (use ref for current value)
            // This prevents "logout" appearance on connection timeouts
            console.log('[Auth] No account returned and none cached - user may need to re-login')
          } else {
            console.log('[Auth] Fetch failed but keeping cached account')
          }
        }
      } catch (err) {
        console.error('[Auth] Error fetching account:', err)
        if (err instanceof Error && err.message === 'NOT_MEMBER') {
          if (mounted) {
            setAuthError('You must be a member of the Raven Discord server to access this site.')
          }
          await supabase.auth.signOut()
        }
      } finally {
        console.log('[Auth] Setting loading false, mounted:', mounted)
        isFetchingRef.current = false
        if (mounted) {
          setLoading(false)
        }
      }
    }

    // Listen for auth changes. The callback stays synchronous - see loadAccount.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        console.log('Auth event:', event)

        if (!mounted) return

        // Handle sign out
        if (event === 'SIGNED_OUT' || !newSession) {
          queryClient.clear()
          setApplicant(null)
          userAccountRef.current = null
          setSession(null)
          setUser(null)
          setUserAccount(null)
          setLoading(false)
          return
        }

        // Applicants get no user and no account - see APPLYING_KEY
        if (isApplying()) {
          setApplicant(applicantFromSession(newSession))
          setLoading(false)
          return
        }

        // During OAuth callback, skip early SIGNED_IN - wait for INITIAL_SESSION
        // The first SIGNED_IN fires before the token is ready for API calls
        if (isOAuthCallback && event === 'SIGNED_IN') {
          console.log('[Auth] Skipping early SIGNED_IN during OAuth callback, waiting for INITIAL_SESSION')
          return
        }

        // Track when we've had INITIAL_SESSION
        if (event === 'INITIAL_SESSION') {
          hasHadInitialSessionRef.current = true
        }

        // Skip TOKEN_REFRESHED events that fire before INITIAL_SESSION
        // These happen during startup when Supabase isn't fully ready yet
        if (event === 'TOKEN_REFRESHED' && !hasHadInitialSessionRef.current) {
          console.log('[Auth] Skipping TOKEN_REFRESHED before INITIAL_SESSION')
          setSession(newSession)
          setUser(newSession.user)
          return
        }

        // For SIGNED_IN on normal page loads, just update session without fetching
        // INITIAL_SESSION already handles the account fetch
        if (event === 'SIGNED_IN' && !isOAuthCallback) {
          console.log('[Auth] SIGNED_IN on page load - updating session only (INITIAL_SESSION handles fetch)')
          setSession(newSession)
          setUser(newSession.user)
          return
        }

        // Handle auth events that need user account fetch
        const shouldFetchAccount = event === 'INITIAL_SESSION' ||
                                   event === 'TOKEN_REFRESHED'

        if (shouldFetchAccount) {
          // Skip if already fetching
          if (isFetchingRef.current) {
            console.log('[Auth] Skipping', event, '- fetch already in progress')
            setSession(newSession)
            setUser(newSession.user)
            return
          }

          // Debounce: skip if we fetched recently (within 5 seconds) for same user
          const now = Date.now()
          if (now - lastFetchTimeRef.current < 5000 && currentUserIdRef.current === newSession.user.id) {
            console.log('[Auth] Skipping', event, '- already fetched recently for this user')
            setSession(newSession)
            setUser(newSession.user)
            setLoading(false)
            return
          }

          console.log('[Auth] Handling', event, '- fetching user account...')
          setSession(newSession)
          setUser(newSession.user)
          lastFetchTimeRef.current = now
          currentUserIdRef.current = newSession.user.id
          isFetchingRef.current = true

          // Defer until the callback has returned and released the auth lock
          setTimeout(() => { void loadAccount(newSession) }, 0)
        }
      }
    )

    // Note: Supabase handles tab visibility changes internally and fires SIGNED_IN
    // We don't need our own visibility handler - it causes duplicate fetches

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [])

  const signInWithDiscord = async () => {
    setApplying(false)
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'discord',
      options: {
        redirectTo: window.location.origin,
        scopes: 'identify guilds guilds.members.read',
      },
    })

    if (error) {
      console.error('Discord sign in error:', error)
      throw error
    }
  }

  /**
   * Confirm a Discord account to send an application. Only `identify`: an
   * applicant is not in the guild, so there are no roles to read.
   * Comes back to the site root, which is the landing page for them.
   */
  const startApplicantSignIn = async () => {
    setApplying(true)
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'discord',
      options: {
        redirectTo: window.location.origin,
        scopes: 'identify',
      },
    })
    if (error) {
      setApplying(false)
      console.error('Discord applicant sign in error:', error)
      throw error
    }
  }

  /** Done applying (sent or given up): drop the session at once */
  const endApplicantSession = async () => {
    setApplying(false)
    setApplicant(null)
    await supabase.auth.signOut()
  }

  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    if (error) {
      console.error('Sign out error:', error)
      throw error
    }
    setUserAccount(null)
  }

  const updateBio = async (bio: string) => {
    if (!user) return

    const { data, error } = await supabase
      .from('user_accounts')
      .update({ bio })
      .eq('id', user.id)
      .select()
      .single()

    if (error) {
      console.error('Failed to update bio:', error)
      throw error
    }

    setUserAccount(data)
  }

  const updateLocation = async (lat: number, lng: number, locationName: string, timezone: string) => {
    if (!user) return

    const { data, error } = await supabase
      .from('user_accounts')
      .update({ location_lat: lat, location_lng: lng, location_name: locationName, timezone })
      .eq('id', user.id)
      .select()
      .single()

    if (error) {
      console.error('Failed to update location:', error)
      throw error
    }

    setUserAccount(data)
  }

  const removeLocation = async () => {
    if (!user) return

    const { data, error } = await supabase
      .from('user_accounts')
      .update({ location_lat: null, location_lng: null, location_name: null, timezone: null })
      .eq('id', user.id)
      .select()
      .single()

    if (error) {
      console.error('Failed to remove location:', error)
      throw error
    }

    setUserAccount(data)
  }

  const isOfficer = userAccount?.is_officer ?? false
  const isApothecary = userAccount?.is_apothecary ?? false

  return (
    <AuthContext.Provider
      value={{
        user,
        applicant,
        session,
        userAccount,
        isOfficer,
        isApothecary,
        loading,
        authError,
        signInWithDiscord,
        startApplicantSignIn,
        endApplicantSession,
        signOut,
        updateBio,
        updateLocation,
        removeLocation,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
